// Tag sets are a registry scoped by board (KULA-REVISE-LAB-ENGINE-PLAN.md §3.5).
//
// "No CAPS tags in an IEB file" used to be a text sweep over forked HTML. Here
// it is structural: a question's `tag` must belong to a set registered for the
// run's board and profile, or the validator blocks the write.
//
// Leah confirmed the IEB sets are internal band-control devices, not board
// vocabulary. The renderer may show them as chips; nothing may present them to
// a learner or teacher as IEB terminology.

import type { ReviseBoard, ReviseProfile } from './types';

export type TagSetKey = 'nsc-level' | 'nsc-source' | 'nsc-essay' | 'nsc-tier' | 'ieb-source' | 'ieb-essay' | 'ieb-tier';

export interface TagSet {
  key: TagSetKey;
  board: ReviseBoard;
  label: string;
  values: readonly string[];
  /** Which values count as lower order for the 60% band target. */
  lowerOrder: readonly string[];
  profiles: readonly ReviseProfile[];
}

export const TAG_SETS: readonly TagSet[] = [
  {
    key: 'nsc-level',
    board: 'nsc',
    label: 'Cognitive level',
    values: ['L1', 'L2', 'L3'],
    lowerOrder: ['L1'],
    profiles: ['paper'],
  },
  // The NSC Study Labs (Leah's lesson engine, CAPS syllabus) carry their own
  // two spines, found in the live files during the migration audit
  // (SPINE = ["Extract","Interpret","Evaluate"] and ["Argue","Evidence","Weigh"]).
  {
    key: 'nsc-source',
    board: 'nsc',
    label: 'Source spine (NSC)',
    values: ['Extract', 'Interpret', 'Evaluate'],
    lowerOrder: ['Extract'],
    profiles: ['lesson', 'skeleton'],
  },
  {
    key: 'nsc-essay',
    board: 'nsc',
    label: 'Essay spine (NSC)',
    values: ['Argue', 'Evidence', 'Weigh'],
    lowerOrder: [],
    profiles: ['lesson', 'skeleton'],
  },
  {
    key: 'nsc-tier',
    board: 'nsc',
    label: 'Knowledge tier',
    values: ['Recall', 'Understand', 'Apply'],
    lowerOrder: ['Recall', 'Understand'],
    profiles: ['check'],
  },
  {
    key: 'ieb-source',
    board: 'ieb',
    label: 'Source spine',
    values: ['Comprehend', 'Apply', 'Analyse', 'Evaluate'],
    lowerOrder: ['Comprehend', 'Apply'],
    profiles: ['lesson', 'skeleton'],
  },
  {
    key: 'ieb-essay',
    board: 'ieb',
    label: 'Essay spine',
    values: ['Stand', 'Evidence', 'Focus', 'Counter'],
    lowerOrder: [],
    profiles: ['lesson', 'skeleton'],
  },
  {
    key: 'ieb-tier',
    board: 'ieb',
    label: 'Knowledge tier',
    values: ['Recall', 'Understand', 'Apply'],
    lowerOrder: ['Recall', 'Understand'],
    profiles: ['check'],
  },
];

export function tagSetsFor(board: ReviseBoard, profile: ReviseProfile): TagSet[] {
  return TAG_SETS.filter((t) => t.board === board && t.profiles.includes(profile));
}

export function findTagSet(board: ReviseBoard, profile: ReviseProfile, tag: string): TagSet | null {
  return tagSetsFor(board, profile).find((t) => t.values.includes(tag)) ?? null;
}

export function isValidTag(board: ReviseBoard, profile: ReviseProfile, tag: string): boolean {
  return findTagSet(board, profile, tag) !== null;
}

/**
 * Whether a tag counts as lower order for the lesson band. Null when the tag
 * is not on a set that participates in the band (the essay spine has no
 * lower-order half, so essay-spine items are excluded from the calculation).
 */
export function isLowerOrder(board: ReviseBoard, profile: ReviseProfile, tag: string): boolean | null {
  const set = findTagSet(board, profile, tag);
  if (!set) return null;
  if (set.lowerOrder.length === 0) return null;
  return set.lowerOrder.includes(tag);
}

/** The NSC level a tag denotes, or null. */
export function nscLevelOf(tag: string | undefined): 1 | 2 | 3 | null {
  if (tag === 'L1') return 1;
  if (tag === 'L2') return 2;
  if (tag === 'L3') return 3;
  return null;
}

/** Every value across every set that is NOT valid for this board — the "wrong board" list. */
export function foreignTags(board: ReviseBoard): string[] {
  return TAG_SETS.filter((t) => t.board !== board).flatMap((t) => [...t.values]);
}
